import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, Form, Input, Select, Row, Col, message, Popconfirm, Tag, Empty, Space, Checkbox, Upload, Image, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, BankOutlined, ClockCircleOutlined, DollarOutlined, PlayCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { projectApi, orderApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const assetTypeOptions = [
  { value: '角色', label: '人物形象' },
  { value: '场景', label: '场景图片' },
  { value: '道具', label: '道具图片' },
  { value: '其他', label: '其他资产' }
];

const { Option } = Select;
const { TextArea } = Input;

const styleOptions = [
  '真人写实', '2D动漫风格', '3D国创', '三渲二', '日式少女漫',
  '龙族传说', '吉卜力', '80s年代', '木叶村', '名侦探柯南',
  '草帽团', '奇幻平涂', '藤本树', '空灵哥特', '美式3D','灌篮高手'
];

const roleColors = {
  'OWNER': 'gold',
  'ADMIN': 'red',
  'DIRECTOR': 'blue',
  'ARTIST': 'green',
  'AIGC_ENGINEER': 'purple',
  'VIEWER': 'default'
};

const roleNames = {
  'OWNER': '创建者',
  'ADMIN': '管理员',
  'DIRECTOR': '导演',
  'ARTIST': '美术',
  'AIGC_ENGINEER': 'AIGC工程师',
  'VIEWER': '参观者'
};

const HomePage = () => {
  const [acceptedProjects, setAcceptedProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [orderForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('orders');
  const [orderAssets, setOrderAssets] = useState([]);
  const [orderAssetType, setOrderAssetType] = useState('角色');
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'creator';

  useEffect(() => {
    loadProjects();
    loadOrders();
  }, [role]);

  const loadProjects = async () => {
    try {
      const response = await projectApi.getAll();
      console.log('API Response:', response.data);
      
      let allProjects = [];
      
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const created = Array.isArray(response.data.created) ? response.data.created : [];
        const shared = Array.isArray(response.data.shared) ? response.data.shared : [];
        allProjects = [...created, ...shared];
      } else if (Array.isArray(response.data)) {
        allProjects = response.data;
      }
      
      console.log('All projects:', allProjects);
      
      setAcceptedProjects(allProjects);
    } catch (error) {
      console.error('加载项目失败:', error);
      message.error('加载项目失败');
      setAcceptedProjects([]);
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await projectApi.delete(id);
      message.success('项目已删除');
      loadProjects();
    } catch (error) {
      message.error('删除项目失败');
    }
  };

  const handleOpenProject = (id) => {
    navigate(`/project/${id}`);
  };

  const loadOrders = async () => {
    try {
      if (role === 'publisher') {
        const response = await orderApi.getAll({ role: 'company' });
        setOrders(response.data.orders);
      } else {
        const response = await orderApi.getPublic();
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('加载订单失败:', error);
      setOrders([]);
    }
  };

  const handleResubmit = async (orderId) => {
    try {
      await orderApi.resubmit(orderId, {});
      message.success('订单已重新提交审核');
      loadOrders();
    } catch (error) {
      message.error('重新提交失败');
      console.error('重新提交失败:', error);
    }
  };

  const handleCreateOrder = async (values) => {
    try {
      const response = await orderApi.create(values);
      const orderId = response.data.order.id;
      
      for (const asset of orderAssets) {
        const formData = new FormData();
        formData.append('file', asset.file);
        formData.append('asset_type', asset.asset_type);
        formData.append('name', asset.name || asset.file_name);
        formData.append('description', asset.description || '');
        await orderApi.uploadAsset(orderId, formData);
      }
      
      setOrderAssets([]);
      message.success('订单发布成功');
      setIsOrderModalVisible(false);
      orderForm.resetFields();
      loadOrders();
    } catch (error) {
      message.error(error.response?.data?.error || '发布订单失败');
    }
  };

  const handleOpenOrder = (id) => {
    navigate(`/order/${id}`);
  };
  const renderProjectCard = (project) => {
    const roles = project.roles || [project.role || 'OWNER'];
    const roleNames_list = project.role_names || [project.role_name || roleNames[project.role] || project.role || 'OWNER'];
    
    const canDelete = project.is_owner === true || roles.includes('OWNER') || project.role === 'OWNER';
    
    const orderInfo = project.order_info;
    const status = orderInfo?.status || project.workflow_status;
    
    const getStatusName = (status) => {
      const statusMap = {
        pending: '待接单',
        accepted: '已接单',
        in_progress: '进行中',
        submitted: '已提交审核',
        approved: '审核通过',
        rejected: '审核失败',
        completed: '已完成',
        cancelled: '已取消',
        script: '剧本创作',
        assets: '资产管理',
        storyboard: '分镜制作',
        production: '视频制作',
        completed: '已完成'
      };
      return statusMap[status] || status;
    };
    
    const getStatusColor = (status) => {
      const colorMap = {
        pending: 'orange',
        accepted: 'blue',
        in_progress: 'processing',
        submitted: 'purple',
        approved: 'green',
        rejected: 'red',
        completed: 'gold',
        cancelled: 'default',
        script: 'blue',
        assets: 'cyan',
        storyboard: 'purple',
        production: 'processing'
      };
      return colorMap[status] || 'default';
    };
    
    return (
      <Col xs={24} sm={12} md={8} lg={6} key={project.id}>
        <Card
          hoverable
          style={{ 
            background: '#1a1a24', 
            border: '1px solid #2a2a3a',
            borderRadius: 12,
            overflow: 'hidden'
          }}
          cover={
            project.cover_image ? (
              <img alt={project.name} src={project.cover_image} style={{ height: 120, objectFit: 'cover' }} />
            ) : (
              <div style={{ height: 120, background: '#252532', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 40 }}>🎬</span>
              </div>
            )
          }
          actions={[
            <Button type="link" style={{ color: '#00d4aa' }} onClick={() => handleOpenProject(project.id)}>打开</Button>,
            canDelete && !project.is_demo ? (
              <Popconfirm
                key="delete"
                title="确定要删除这个项目吗？"
                onConfirm={() => handleDeleteProject(project.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger>删除</Button>
              </Popconfirm>
            ) : null,
          ].filter(Boolean)}
        >
          <Card.Meta
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#e8e8ec', fontSize: 14 }}>{project.name}</span>
                <Tag color={getStatusColor(status)} size="small">{getStatusName(status)}</Tag>
              </div>
            }
            description={
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Tag color="#00d4aa" size="small">{project.style}</Tag>
                  {orderInfo?.duration_minutes && (
                    <span style={{ color: '#a0a0b0', fontSize: 12 }}>{orderInfo.duration_minutes}分钟</span>
                  )}
                </div>
                {orderInfo && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <DollarOutlined style={{ color: '#f5a623', fontSize: 12 }} />
                      <span style={{ color: '#f5a623', fontWeight: 'bold' }}>{orderInfo.price_per_minute}积分/分钟</span>
                      <span style={{ color: '#e8e8ec' }}>共计 {orderInfo.total_price} 积分</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <ClockCircleOutlined style={{ color: '#a0a0b0', fontSize: 12 }} />
                      <span style={{ color: '#a0a0b0', fontSize: 12 }}>{orderInfo.delivery_days}天交付</span>
                    </div>
                    {orderInfo.company_user && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <UserOutlined style={{ color: '#6c6c7d', fontSize: 12 }} />
                        <span style={{ color: '#6c6c7d', fontSize: 12 }}>发布者：{orderInfo.company_user.nickname}</span>
                      </div>
                    )}
                  </>
                )}
                {!orderInfo && (
                  <>
                    {project.owner_nickname && (
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6c6c7d' }}>
                        创建者：{project.owner_nickname}
                      </p>
                    )}
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6c6c7d' }}>
                      {new Date(project.updated_at).toLocaleDateString()}
                    </p>
                  </>
                )}
              </div>
            }
          />
        </Card>
      </Col>
    );
  };



  const styleOptionsForOrder = ['仿真人', '2D动漫风格', '动态漫', '3D国创', '三渲二', '日式少女漫', '吉卜力'];
  
  const statusColors = {
    pending: 'orange',
    accepted: 'blue',
    in_progress: 'processing',
    submitted: 'purple',
    approved: 'green',
    rejected: 'red',
    completed: 'gold',
    cancelled: 'default'
  };

  const statusNames = {
    pending: '待接单',
    accepted: '已接单',
    in_progress: '进行中',
    submitted: '已提交审核',
    approved: '审核通过',
    rejected: '审核失败',
    completed: '已完成',
    cancelled: '已取消'
  };

  const renderOrderCard = (order) => (
    <Col xs={24} sm={12} md={8} key={order.id}>
      <Card
        hoverable
        style={{ 
          background: '#1a1a24', 
          border: '1px solid #2a2a3a',
          borderRadius: 12,
          cursor: 'pointer'
        }}
        onClick={() => handleOpenOrder(order.id)}
      >
        <Card.Meta
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#e8e8ec', fontSize: 14 }}>{order.title}</span>
              <Tag color={statusColors[order.status]} size="small">{statusNames[order.status]}</Tag>
            </div>
          }
          description={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Tag color="#00d4aa" size="small">{order.style}</Tag>
                <span style={{ color: '#a0a0b0', fontSize: 12 }}>{order.duration_minutes}分钟</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <DollarOutlined style={{ color: '#f5a623', fontSize: 12 }} />
                <span style={{ color: '#f5a623', fontWeight: 'bold' }}>{order.price_per_minute}积分/分钟</span>
                <span style={{ color: '#e8e8ec' }}>共计 {order.total_price} 积分</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClockCircleOutlined style={{ color: '#a0a0b0', fontSize: 12 }} />
                <span style={{ color: '#a0a0b0', fontSize: 12 }}>{order.delivery_days}天交付</span>
              </div>
            </div>
          }
        />
      </Card>
    </Col>
  );

  return (
    <div style={{ padding: 24, background: '#0d0d12', minHeight: '100%' }}>
      {role === 'creator' && (
        <>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h1 style={{ margin: 0, color: '#e8e8ec' }}>{activeTab === 'projects' ? '我的项目' : '接单平台'}</h1>
              </div>
            
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type={activeTab === 'projects' ? 'primary' : 'default'}
                onClick={() => setActiveTab('projects')}
                style={{ 
                  background: activeTab === 'projects' ? '#00d4aa' : '#252532', 
                  borderColor: activeTab === 'projects' ? '#00d4aa' : '#2a2a3a',
                  color: activeTab === 'projects' ? '#0d0d12' : '#e8e8ec'
                }}
              >
                <PlayCircleOutlined /> 项目管理
              </Button>
              <Button
                type={activeTab === 'orders' ? 'primary' : 'default'}
                onClick={() => setActiveTab('orders')}
                style={{ 
                  background: activeTab === 'orders' ? '#00d4aa' : '#252532', 
                  borderColor: activeTab === 'orders' ? '#00d4aa' : '#2a2a3a',
                  color: activeTab === 'orders' ? '#0d0d12' : '#e8e8ec'
                }}
              >
                <BankOutlined /> 接单平台
              </Button>
            </div>
          </div>

          {activeTab === 'projects' ? (
            <>
              <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PlayCircleOutlined style={{ fontSize: 18, color: '#00d4aa' }} />
                  <h2 style={{ margin: 0, fontSize: 18, color: '#e8e8ec' }}>已接单项目</h2>
                  <Tag color="#00d4aa" style={{ color: '#0d0d12', fontWeight: 'bold' }}>{acceptedProjects.length}</Tag>
                </div>
                
                {acceptedProjects.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {acceptedProjects.map(project => renderProjectCard(project))}
                  </Row>
                ) : (
                  <Empty description="还没有已接单项目" style={{ padding: 40, background: '#1a1a24', borderRadius: 8 }} />
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BankOutlined style={{ fontSize: 18, color: '#00d4aa' }} />
                  <h2 style={{ margin: 0, fontSize: 18, color: '#e8e8ec' }}>待接单任务</h2>
                  <Tag color="#00d4aa" style={{ color: '#0d0d12', fontWeight: 'bold' }}>{orders.length}</Tag>
                </div>
                
                {orders.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {orders.map(order => renderOrderCard(order))}
                  </Row>
                ) : (
                  <Empty description="暂无待接单任务" style={{ padding: 40, background: '#1a1a24', borderRadius: 8 }} />
                )}
              </div>
            </>
          )}
        </>
      )}

      {role === 'publisher' && (
        <>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h1 style={{ margin: 0, color: '#e8e8ec' }}>任务发布</h1>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => {
                  if (!user.is_active) {
                    message.warning('等待管理员进行资质审核');
                    return;
                  }
                  setIsOrderModalVisible(true);
                }}
                disabled={!user.is_active}
                style={{ 
                  background: user.is_active ? '#00d4aa' : '#4a4a5a', 
                  borderColor: user.is_active ? '#00d4aa' : '#4a4a5a',
                  cursor: user.is_active ? 'pointer' : 'not-allowed'
                }}
              >
                {user.is_active ? '发布任务' : '等待审核'}
              </Button>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BankOutlined style={{ fontSize: 18, color: '#00d4aa' }} />
              <h2 style={{ margin: 0, fontSize: 18, color: '#e8e8ec' }}>我发布的任务</h2>
              <Tag color="#00d4aa" style={{ color: '#0d0d12', fontWeight: 'bold' }}>{orders.length}</Tag>
            </div>
            
            {orders.length > 0 ? (
              <Row gutter={[16, 16]}>
                {orders.map(order => (
                  <Col xs={24} sm={12} md={8} key={order.id}>
                    <Card
                      hoverable
                      style={{ 
                        background: '#1a1a24', 
                        border: '1px solid #2a2a3a',
                        borderRadius: 12,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOpenOrder(order.id)}
                    >
                      <Card.Meta
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#e8e8ec', fontSize: 14 }}>{order.title}</span>
                            <Tag color={statusColors[order.status]} size="small">{statusNames[order.status]}</Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <Tag color="#00d4aa" size="small">{order.style}</Tag>
                              <span style={{ color: '#a0a0b0', fontSize: 12 }}>{order.duration_minutes}分钟</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                              <DollarOutlined style={{ color: '#f5a623', fontSize: 12 }} />
                              <span style={{ color: '#f5a623', fontWeight: 'bold' }}>{order.price_per_minute}积分/分钟</span>
                              <span style={{ color: '#e8e8ec' }}>共计 {order.total_price} 积分</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <ClockCircleOutlined style={{ color: '#a0a0b0', fontSize: 12 }} />
                              <span style={{ color: '#a0a0b0', fontSize: 12 }}>{order.delivery_days}天交付</span>
                            </div>
                            {order.freelancer_user && (
                              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #2a2a3a' }}>
                                <span style={{ color: '#00d4aa', fontSize: 12 }}>接单者：{order.freelancer_user.nickname}</span>
                              </div>
                            )}
                            {order.status === 'rejected' && order.reject_reason && (
                              <div style={{ marginTop: 8, padding: 8, background: '#3a2a2a', borderRadius: 4 }}>
                                <span style={{ color: '#ff6b6b', fontSize: 12 }}>拒绝理由：{order.reject_reason}</span>
                              </div>
                            )}
                            {order.status === 'rejected' && (
                              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #2a2a3a' }}>
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResubmit(order.id);
                                  }}
                                  style={{ background: '#00d4aa', borderColor: '#00d4aa' }}
                                >
                                  再次提交
                                </Button>
                              </div>
                            )}
                          </div>
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty description="暂无发布的任务" style={{ padding: 40, background: '#1a1a24', borderRadius: 8 }} />
            )}
          </div>
        </>
      )}

      

      {/* 发布订单弹窗 */}
      <Modal
        title="发布AI短剧制作任务"
        visible={isOrderModalVisible}
        onCancel={() => setIsOrderModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={orderForm} onFinish={handleCreateOrder} layout="vertical">
          <Form.Item
            name="title"
            label="任务标题"
            rules={[{ required: true, message: '请输入任务标题' }]}
          >
            <Input placeholder="例如：制作10分钟仿真人短剧" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="任务描述"
          >
            <TextArea rows={3} placeholder="请描述任务的具体要求" />
          </Form.Item>
          
          <Form.Item
            name="style"
            label="风格选择"
            rules={[{ required: true, message: '请选择风格' }]}
          >
            <Select>
              {styleOptionsForOrder.map(style => (
                <Option key={style} value={style}>{style}</Option>
              ))}
            </Select>
          </Form.Item>
          
          <Row gutter={[16, 0]}>
            <Col xs={12}>
              <Form.Item
                name="duration_minutes"
                label="视频时长（分钟）"
                rules={[{ required: true, message: '请输入时长' }]}
              >
                <Input type="number" placeholder="例如：5" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="delivery_days"
                label="交付周期（天）"
                rules={[{ required: true, message: '请输入交付周期' }]}
              >
                <Input type="number" placeholder="例如：7" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="price_per_minute"
            label="单价（积分/分钟）"
            rules={[
              { required: true, message: '请输入单价' },
              { type: 'number', min: 100, max: 5000, message: '单价必须在100-5000积分之间' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value % 10 === 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('单价最小变动单位为10'));
                },
              }),
            ]}
            initialValue={500}
          >
            <InputNumber
              placeholder="请输入单价"
              min={100}
              max={5000}
              step={10}
              style={{ width: '100%' }}
              formatter={(value) => `${value} 积分/分钟`}
              parser={(value) => value.replace(/\s*积分\/分钟/, '')}
            />
          </Form.Item>
          
          <Form.Item
            name="script_content"
            label="剧本内容"
          >
            <TextArea rows={6} placeholder="请粘贴剧本内容，或说明剧本将由接单者创作" />
          </Form.Item>
          
          <Form.Item
            name="assets_description"
            label="资产说明"
          >
            <TextArea rows={3} placeholder="请描述提供的资产类型和要求" />
          </Form.Item>
          
          <div style={{ background: '#252532', padding: 16, borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <span style={{ color: '#e8e8ec', fontWeight: 'bold' }}>上传资产文件</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Select
                          value={orderAssetType}
                          onChange={setOrderAssetType}
                          style={{ width: 120, background: '#1a1a24', borderColor: '#2a2a3a', color: '#e8e8ec' }}
                        >
                          {assetTypeOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                        <Upload
                          beforeUpload={(file) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setOrderAssets(prev => [...prev, {
                                file: file,
                                asset_type: orderAssetType,
                                file_name: file.name,
                                name: '',
                                description: '',
                                preview: e.target.result
                              }]);
                            };
                            reader.readAsDataURL(file);
                            return false;
                          }}
                          accept="image/*"
                          showUploadList={false}
                        >
                          <Button icon={<PlusOutlined />} style={{ background: '#00d4aa', borderColor: '#00d4aa' }}>
                            上传资产
                          </Button>
                        </Upload>
                      </div>
                    </div>
                    {orderAssets.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                {orderAssets.map((asset, index) => (
                  <div key={index} style={{ width: 200, background: '#1a1a24', borderRadius: 8, padding: 12 }}>
                    <div style={{ position: 'relative', width: '100%', height: 120, marginBottom: 8 }}>
                      <Image
                        src={asset.preview}
                        alt={asset.file_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                      />
                      <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', padding: '2px 8px', borderRadius: 4 }}>
                        <span style={{ color: '#fff', fontSize: 12 }}>{asset.asset_type}</span>
                      </div>
                    </div>
                    <Input
                      placeholder="资产名称"
                      value={asset.name}
                      onChange={(e) => {
                        const newAssets = [...orderAssets];
                        newAssets[index].name = e.target.value;
                        setOrderAssets(newAssets);
                      }}
                      style={{ background: '#1a1a24', borderColor: '#2a2a3a', color: '#e8e8ec', marginBottom: 8 }}
                      size="small"
                    />
                    <TextArea
                      placeholder="主体描述（可选）"
                      value={asset.description}
                      onChange={(e) => {
                        const newAssets = [...orderAssets];
                        newAssets[index].description = e.target.value;
                        setOrderAssets(newAssets);
                      }}
                      style={{ background: '#1a1a24', borderColor: '#2a2a3a', color: '#e8e8ec', marginBottom: 8 }}
                      size="small"
                      rows={2}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      style={{ width: '100%' }}
                      onClick={() => setOrderAssets(prev => prev.filter((_, i) => i !== index))}
                    >
                      删除
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="暂无资产文件" />
            )}
          </div>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              发布任务
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HomePage;
